import React, {createContext, useContext} from "react";
import {FormSection, StatusBadge, inputClass} from "../SettingsUI";
import {decodeValue, deviceConnection, formatSince} from "./posConfig";

// Rows of the POS device settings screen. A row given `bind={key, path?}` (an
// EDITABLE_KEYS key, and for a JSON key the field inside it) turns into an
// editor in edit mode and shows « En attente » while a backoffice change to it
// hasn't been applied by the device yet.

const fieldClass = inputClass(false);

// {editing, draft, setDraft, flat (backup + pending), backupFlat, pending}
export const PosEditContext = createContext({editing: false});

const readPath = (value, path) => (path ? value?.[path] : value);

export const useBinding = (bind) => {
    const ctx = useContext(PosEditContext);
    if (!bind || !ctx.flat) return null;
    const {key, path} = bind;
    const current = key in (ctx.draft || {}) ? ctx.draft[key] : decodeValue(key, ctx.flat[key]);

    const pendingSettings = ctx.pending?.settings || {};
    let pending = false;
    if ((ctx.pending?.removeKeys || []).includes(key)) {
        pending = true;
    } else if (key in pendingSettings) {
        pending = !path || readPath(decodeValue(key, pendingSettings[key]), path)
            !== readPath(decodeValue(key, ctx.backupFlat[key]), path);
    }

    return {
        editing: ctx.editing,
        pending,
        value: readPath(current, path),
        onChange: (value) => ctx.setDraft(draft => {
            const base = key in draft ? draft[key] : decodeValue(key, ctx.flat[key]);
            return {...draft, [key]: path ? {...base, [path]: value} : value};
        }),
    };
};

const NotSet = () => <span className="text-gray-400 dark:text-gray-500 italic">Par défaut</span>;

export const PendingBadge = () => (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
        En attente
    </span>
);

// List of label / value rows under a FormSection title
export const InfoList = ({title, description, children}) => (
    <FormSection title={title} description={description}>
        <dl className="divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
            {children}
        </dl>
    </FormSection>
);

export const InfoRow = ({label, hint, pending, children}) => (
    <div className="flex items-center justify-between gap-4 py-3">
        <dt className="min-w-0">
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                {label}
                {pending && <PendingBadge/>}
            </span>
            {hint && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hint}</span>}
        </dt>
        <dd className="text-sm text-right text-gray-900 dark:text-gray-100 min-w-0 break-words">
            {children === undefined || children === null || children === "" ? <NotSet/> : children}
        </dd>
    </div>
);

export const Toggle = ({checked, onChange, label}) => (
    <span className="relative inline-flex flex-shrink-0">
        <input type="checkbox" className="sr-only peer" aria-label={label} checked={!!checked}
               onChange={(e) => onChange(e.target.checked)}/>
        <span className="w-11 h-6 rounded-full bg-gray-300 dark:bg-gray-600 peer-checked:bg-primary transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2 pointer-events-none"/>
        <span className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5 pointer-events-none"/>
    </span>
);

// On/off value; undefined = never saved on the device (POS default applies)
export const BoolRow = ({label, hint, value, bind, on = "Activé", off = "Désactivé"}) => {
    const b = useBinding(bind);
    if (b?.editing) {
        return (
            <label className="flex items-center justify-between gap-4 py-3 cursor-pointer">
                <span className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        {label}
                        {b.pending && <PendingBadge/>}
                    </span>
                    {hint && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hint}</span>}
                </span>
                <Toggle label={label} checked={b.value} onChange={b.onChange}/>
            </label>
        );
    }
    return (
        <InfoRow label={label} hint={hint} pending={b?.pending}>
            {value === undefined ? undefined : <StatusBadge active={value} on={on} off={off}/>}
        </InfoRow>
    );
};

// Value picked among fixed options ({value: label})
export const ChoiceRow = ({label, hint, value, options, bind}) => {
    const b = useBinding(bind);
    return (
        <InfoRow label={label} hint={hint} pending={b?.pending}>
            {b?.editing ? (
                <select aria-label={label} className={`${fieldClass} w-auto min-w-[12rem]`}
                        value={b.value ?? ""} onChange={(e) => b.onChange(e.target.value)}>
                    {Object.entries(options).map(([v, text]) => <option key={v} value={v}>{text}</option>)}
                </select>
            ) : value === undefined || value === null || value === "" ? undefined : (options[value] || String(value))}
        </InfoRow>
    );
};

export const TextRow = ({label, hint, value, bind, type = "text"}) => {
    const b = useBinding(bind);
    return (
        <InfoRow label={label} hint={hint} pending={b?.pending}>
            {b?.editing ? (
                <input type={type} aria-label={label} className={`${fieldClass} w-64 max-w-full`}
                       value={b.value ?? ""} onChange={(e) => b.onChange(e.target.value)}/>
            ) : value}
        </InfoRow>
    );
};

export const ColorValue = ({color}) => !color ? null : (
    <span className="inline-flex items-center gap-2">
        <span className="w-4 h-4 rounded border border-gray-300 dark:border-gray-600" style={{backgroundColor: color}}/>
        <span className="font-mono text-xs">{color}</span>
    </span>
);

// Pastille « Connectée / Vue il y a … » d'une caisse sur ce compte.
export const ConnectionBadge = ({config}) => {
    const {state, lastSeen} = deviceConnection(config);
    const styles = {
        online: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
        away: "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300",
        unknown: "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400",
    };
    const dot = {online: "bg-green-500", away: "bg-gray-400", unknown: "bg-gray-300 dark:bg-gray-500"};
    const label = state === "online" ? "Connectée"
        : state === "away" ? `Vue ${formatSince(lastSeen)}`
        : "Jamais vue";
    return (
        <span title={lastSeen ? lastSeen.toLocaleString("fr-FR") : undefined}
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${styles[state]}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${dot[state]}`}/>
            {label}
        </span>
    );
};

export const EmptyNote = ({children}) => (
    <p className="text-sm text-gray-500 dark:text-gray-400 py-3">{children}</p>
);
